import { useEffect, useState } from "react";
import { Check, Link2, Mail, Share2 } from "lucide-react";
import { getAccessToken } from "@/lib/supabase-lazy";
import { getReferralStatus } from "@/lib/referrals.functions";
import { SHARE_MESSAGE, absoluteUrl, shareText, withReferral } from "@/lib/share";

type Props = {
  url: string;
  title: string;
  label?: string;
  className?: string;
  /** Override the default share copy. */
  message?: string;
};

export function ShareButtons({
  url,
  title,
  label = "Share this",
  className = "",
  message = SHARE_MESSAGE,
}: Props) {
  const [copied, setCopied] = useState(false);
  const [refCode, setRefCode] = useState<string | null>(null);

  // Signed-in operators share their own referral link; everyone else shares the plain URL.
  useEffect(() => {
    let alive = true;
    void (async () => {
      const token = await getAccessToken();
      if (!token || !alive) return;
      try {
        const status = await getReferralStatus();
        if (alive) setRefCode(status.code ?? null);
      } catch {
        /* not fatal — fall back to the plain link */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Referral links point at /auth so the code survives the site gate redirect.
  const href = refCode
    ? withReferral(absoluteUrl("/auth"), refCode)
    : absoluteUrl(url);
  const text = shareText(href, message);
  const e = encodeURIComponent;

  const targets = [
    { name: "X", href: `https://twitter.com/intent/tweet?text=${e(message)}&url=${e(href)}` },
    { name: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${e(href)}` },
    {
      name: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${e(href)}&quote=${e(message)}`,
    },
  ];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  const nativeShare = async () => {
    if (typeof navigator !== "undefined" && "share" in navigator) {
      try {
        await navigator.share({ title, text: message, url: href });
      } catch {
        /* dismissed */
      }
    } else {
      void copy();
    }
  };

  const item =
    "inline-flex items-center gap-2 rounded-[4px] border border-border/70 px-3 py-1.5 text-xs uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground";

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <span className="mr-1 text-xs uppercase tracking-[0.18em] text-muted-foreground/70">
        {label}
      </span>
      {targets.map((t) => (
        <a
          key={t.name}
          href={t.href}
          target="_blank"
          rel="noopener noreferrer"
          className={item}
          aria-label={`Share on ${t.name}`}
        >
          {t.name}
        </a>
      ))}
      <a
        href={`mailto:?subject=${e(title)}&body=${e(text)}`}
        className={item}
        aria-label="Share by email"
      >
        <Mail className="h-3.5 w-3.5" aria-hidden />
        Email
      </a>
      <button type="button" onClick={copy} className={item} aria-label="Copy link">
        {copied ? <Check className="h-3.5 w-3.5" aria-hidden /> : <Link2 className="h-3.5 w-3.5" aria-hidden />}
        {copied ? "Copied" : "Copy link"}
      </button>
      <button
        type="button"
        onClick={nativeShare}
        className={`${item} sm:hidden`}
        aria-label="Share via device"
      >
        <Share2 className="h-3.5 w-3.5" aria-hidden />
        Share
      </button>
    </div>
  );
}
